/** @jsxImportSource @emotion/react */
import { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import Spinner from 'react-bootstrap/Spinner'
import MaskedInput from 'react-text-mask'
import { toast } from 'react-toastify'

import * as generalHelper from 'helpers/generalHelper'
import * as api from 'helpers/api'
import Button from 'components/atoms/Button'
import Input from 'components/atoms/Input'
import TitleLead from 'components/atoms/TitleLead'
import TabHeader from 'components/atoms/TabHeader'
import { phoneNumberMask } from 'helpers/masks'
import { formatPhoneNumber } from 'helpers/format'
import { confirm } from 'components/molecules/Confirmation'
import { useSendPatientInstructionsMutation } from 'store/services/dmeOrder'
import { Order } from 'store/types'
import routes from '../../constants/routes'

const MODELS_WITH_INSTRUCTIONS = new Set([
  '920m', '920mp', 'n2500', 'n8500',
  'c50f', 'c50i',
  'capnostream35', 'checkmeo2', 'checkmeo2-ppg',
  'n3150', 'n7500', 'nlsii',
  'pc900b',
  'rad8', 'rad97',
  'tsot',
])

interface IProps {
  order: Order
  stateHelpers: any
}

const InstructionsTab = ({ order, stateHelpers }: IProps) => {
  const { orderId } = useParams()
  const [lang, setLang] = useState<'en' | 'es'>('en')
  const [email, setEmail] = useState('')
  const [phone, setPhone] = useState('')
  const [loadingPdf, setLoadingPdf] = useState(false)
  const [lastSent, setLastSent] = useState<{ method: 'email' | 'sms'; destination: string; time: Date } | null>(null)

  const [sendInstructions, { isLoading: isSending }] = useSendPatientInstructionsMutation()

  useEffect(() => {
    setEmail(order?.patient?.email || '')
  }, [order?.patient?.email])

  useEffect(() => {
    const mobile = order?.patient?.mobilePhone
    if (mobile) {
      setPhone(mobile.replace(/^\+1/, ''))
    }
  }, [order?.patient?.mobilePhone])

  const hasDevice = !!(order?.device?.id || order?.thirdPartyDevice || order?.selectedSessions?.length)
  const deviceModel = order?.device?.model?.toLowerCase()
  const hasInstructions = !!deviceModel && MODELS_WITH_INSTRUCTIONS.has(deviceModel)

  const handlePrintPdf = async () => {
    setLoadingPdf(true)
    try {
      const result = await api.get(
        `/dme/orders/${orderId}/device-instruction`,
        { lang },
        true,
      )
      const url = window.URL.createObjectURL(new Blob([result], { type: 'application/pdf' }))
      window.open(url, '_blank')
    } catch (e) {
      toast.error('Failed to generate instructions PDF. Please ensure a device is assigned to this order.')
    } finally {
      setLoadingPdf(false)
    }
  }

  const handleSendEmail = async () => {
    const destination = email || order?.patient?.email
    if (!destination || !orderId) return

    try {
      await sendInstructions({
        orderId: Number(orderId),
        method: 'email',
        lang,
        email: destination,
      }).unwrap()
      setLastSent({ method: 'email', destination, time: new Date() })
      toast.success(`Instructions sent to ${destination}`)
    } catch (e) {
      toast.error('Failed to send instructions. Please try again.')
    }
  }

  const handleSendSms = () => {
    const formattedPhone = `+1${(phone || order?.patient?.mobilePhone || '').replace(/[^0-9]/g, '')}`
    if (!formattedPhone || formattedPhone === '+1' || !orderId) return

    confirm('', {
      title: 'Patient Consent?',
      description: 'Has patient consent been obtained to send device instructions via SMS?',
      yesAction: 'Yes, Send SMS',
      noAction: 'No, Cancel',
    })
      .then(async () => {
        try {
          await sendInstructions({
            orderId: Number(orderId),
            method: 'sms',
            lang,
            phone: formattedPhone,
          }).unwrap()
          setLastSent({ method: 'sms', destination: formattedPhone, time: new Date() })
          toast.success('Instructions sent via SMS')
        } catch (e) {
          toast.error('Failed to send SMS. Please ensure the patient has a valid mobile number.')
        }
      })
      .catch(() => {})
  }

  return (
    <div className="container">
      <TabHeader
        title="PATIENT INSTRUCTIONS"
        back={`${routes.index}${routes.order.edit}${orderId}/Documents`}
      />

      {!hasDevice && (
        <div className="alert alert-default-warning mt-3" role="alert">
          No device is currently assigned to this order. Instructions can only be sent once a device
          has been assigned.
        </div>
      )}
      {hasDevice && !hasInstructions && (
        <div className="alert alert-default-info mt-3" role="alert">
          No patient instructions are available for the device assigned to this order (
          <strong>{order?.device?.model || 'unknown'}</strong>).
        </div>
      )}

      <div className="row mt-3">
        <div className="col-sm-12 mb-3">
          <TitleLead label="Select Language" />
          <div className="btn-group mt-2" role="group">
            <button
              type="button"
              className={`btn ${lang === 'en' ? 'btn-primary' : 'btn-outline-primary'}`}
              onClick={() => setLang('en')}>
              English
            </button>
            <button
              type="button"
              className={`btn ${lang === 'es' ? 'btn-primary' : 'btn-outline-primary'}`}
              onClick={() => setLang('es')}>
              Spanish
            </button>
          </div>
        </div>
      </div>

      <div className="row mt-2">
        <div className="col-sm-6 col-md-4 d-flex flex-column">
          <div className="text-bold mt-2 mb-2">
            <TitleLead label="Print / View PDF" />
          </div>
          <div className="mt-2">
            {generalHelper.t(
              'Open the instructions PDF in a new tab where it can be printed or saved.',
            )}
          </div>
          <div className="mt-2 d-flex align-items-end" style={{ flex: 1 }}>
            <Button
              disabled={loadingPdf || !hasDevice || !hasInstructions}
              className="btn btn-lg btn-primary btn-block"
              label="View / Print PDF"
              onClick={handlePrintPdf}>
              {loadingPdf ? (
                <Spinner
                  animation="border"
                  size="sm"
                  role="status"
                  aria-hidden="true"
                  className="mr-3 mb-2"
                />
              ) : (
                <></>
              )}
            </Button>
          </div>
        </div>

        <div className="col-sm-6 col-md-4 d-flex flex-column">
          <div className="text-bold mt-2 mb-2">
            <TitleLead label="Send via Email" />
          </div>
          <div className="mt-2">
            {generalHelper.t(
              'The patient will receive the instructions PDF as a link in their email.',
            )}
          </div>
          <div className="mt-2 d-flex justify-content-end flex-column" style={{ flex: 1 }}>
            <Input
              value={email}
              type="email"
              handleChange={(e) => setEmail(e.target.value)}
              className="form-control mb-2"
              placeholder="Email address"
            />
            <Button
              disabled={(!email && !order?.patient?.email) || isSending || !hasDevice || !hasInstructions}
              className="btn btn-lg btn-primary btn-block"
              label="Send via Email"
              onClick={handleSendEmail}
            />
          </div>
        </div>

        <div className="col-sm-6 col-md-4 d-flex flex-column">
          <div className="text-bold mt-2 mb-2">
            <TitleLead label="Send via SMS" />
          </div>
          <div className="mt-2">
            {generalHelper.t(
              'The patient will receive an SMS with a link to download their instructions PDF.',
            )}
          </div>
          <div className="mt-2 d-flex justify-content-end flex-column" style={{ flex: 1 }}>
            <MaskedInput
              value={phone}
              type="text"
              onChange={(e) => setPhone(e.target.value)}
              mask={phoneNumberMask}
              className="form-control mb-2"
              placeholder="Mobile phone"
            />
            <Button
              disabled={(!phone && !order?.patient?.mobilePhone) || isSending || !hasDevice || !hasInstructions}
              className="btn btn-lg btn-primary btn-block"
              label="Send via SMS"
              onClick={handleSendSms}
            />
          </div>
        </div>
      </div>

      {lastSent && (
        <div className="row pt-3">
          <div className="col-sm-12" style={{ opacity: 0.7 }}>
            <span className="text-success">
              <i className="fa fa-check mr-1" />
              Instructions sent via {lastSent.method === 'email' ? 'email' : 'SMS'} to{' '}
              <strong>
                {lastSent.method === 'sms'
                  ? formatPhoneNumber(lastSent.destination)
                  : lastSent.destination}
              </strong>{' '}
              at {lastSent.time.toLocaleTimeString()}
            </span>
          </div>
        </div>
      )}
    </div>
  )
}

export default InstructionsTab
